<template>
    <div class="ce-page">
        <!-- Header -->
        <div class="ce-header">
            <div class="ce-header-left">
                <button class="ce-back-btn" @click="cancelEdit"><i class="fas fa-arrow-left"></i></button>
                <div>
                    <h1 class="ce-title">{{ editMode ? (name || 'Company') : 'Create Company' }}</h1>
                    <p class="ce-subtitle" v-if="editMode">Edit company details and payroll defaults</p>
                    <p class="ce-subtitle" v-else>Set up a new company profile</p>
                </div>
            </div>
        </div>

        <!-- Tabs -->
        <div class="ce-tabs">
            <button
                v-for="tab of Object.values(COMPANY_EDIT_TABS)"
                :key="tab.name"
                :class="['ce-tab', { 'ce-tab-active': companyEditTab.name === tab.name }]"
                @click="handleTabChange(tab)"
            >
                <i :class="tab.name === 'COMPANY' ? 'fas fa-building' : 'fas fa-money-bill-wave'" class="ce-tab-icon"></i>
                {{ tab.value }}
            </button>
        </div>

        <!-- COMPANY TAB -->
        <div v-if="companyEditTab.name == COMPANY_EDIT_TABS.COMPANY.name">
            <div v-if="!editMode || editData">
                <!-- Company Info Section -->
                <div class="ce-section">
                    <div class="ce-section-head">
                        <i class="fas fa-info-circle ce-section-icon"></i>
                        <span>Company Information</span>
                    </div>
                    <div class="ce-grid">
                        <div class="ce-field">
                            <custom-input v-model="name" label="Company Name*" />
                        </div>
                        <div class="ce-field-full">
                            <CountryStateCitySelector
                                v-if="!editMode || editData"
                                :countryId="country"
                                :stateId="state"
                                :cityId="city"
                                @country="handleCountryUpdate"
                                @state="state = $event"
                                @city="city = $event"
                            />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="address" label="Address*" />
                        </div>
                        <div class="ce-field">
                            <custom-input type="number" v-model="addressPincode" label="Address Pincode*" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="tanNo" label="TAN No" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="registrationNo" label="Registration No" />
                        </div>
                        <div class="ce-field">
                            <label class="ce-label">Work Locations</label>
                            <SelectDropdown
                                v-if="workLocations.length"
                                :id="`form-work-location`"
                                :options="workLocations.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="true"
                                field="form-work-location"
                                :value="workLocationIds"
                                @change="workLocationIds = $event"
                            />
                        </div>
                    </div>
                </div>

                <!-- Contact Section -->
                <div class="ce-section">
                    <div class="ce-section-head">
                        <i class="fas fa-phone-alt ce-section-icon"></i>
                        <span>Contact Details</span>
                    </div>
                    <div class="ce-grid">
                        <div class="ce-field">
                            <custom-input v-model="contactNumber" label="Contact Number*" type="number" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="contactEmail" label="Contact Email*" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="contactWebsite" label="Contact Website" />
                        </div>
                    </div>
                </div>

                <!-- Bank Details Section -->
                <div class="ce-section">
                    <div class="ce-section-head">
                        <i class="fas fa-university ce-section-icon"></i>
                        <span>Bank Details</span>
                    </div>
                    <div class="ce-grid">
                        <div class="ce-field">
                            <label class="ce-label">Bank</label>
                            <SelectDropdown
                                v-if="bankMasters.length"
                                :id="`bankMasterId`"
                                :options="bankMasters.map((item) => ({ id: item.id, title: item.name }))"
                                field="bankMasterId"
                                :value="bankMasterId"
                                @change="bankMasterId = $event"
                            />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="bankAccountNumber" label="Bank Account Number" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="bankAccountBankName" label="Bank Account Name" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="bankAccountBranch" label="Bank Account Branch" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="bankAccountIfsc" label="Bank Account IFSC" />
                        </div>
                        <div class="ce-field">
                            <custom-input v-model="bankAccountSwiftId" label="Bank Account Swift ID" />
                        </div>
                    </div>
                </div>

                <!-- Action bar -->
                <div class="ce-actions">
                    <button class="ce-btn ce-btn-secondary" @click="cancelEdit">
                        <i class="fas fa-times"></i> Cancel
                    </button>
                    <button class="ce-btn ce-btn-primary" @click="createOrUpdate" :disabled="processing">
                        <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                        {{ processing ? 'Saving...' : editMode ? 'Update Company' : 'Create Company' }}
                    </button>
                </div>
            </div>
            <div v-else class="ce-loading">
                <i class="fas fa-circle-notch fa-spin"></i>
                <span>Loading company details...</span>
            </div>
        </div>

        <!-- PAYROLL TAB -->
        <div v-if="companyEditTab.name == COMPANY_EDIT_TABS.PAYROLL.name">
            <PayheadValuesEditForm :companyId="$route.params.id" />
        </div>
    </div>
</template>

<script>
import { http, serverURL } from "@/utils/http"
import { validateEmail, validatePhone } from "@/utils/common"
import CustomInput from "../../components/crud/custom-input.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import CountryStateCitySelector from "../../components/UI/CountryStateCitySelector.vue"
import { mapState } from "vuex"
import PayheadValuesEditForm from "../../components/payroll/PayheadValuesEditForm.vue"

const COMPANY_EDIT_TABS = {
    COMPANY: { name: "COMPANY", value: "Company Details" },
    PAYROLL: { name: "PAYROLL", value: "Payroll Defaults" },
}

export default {
    data() {
        return {
            COMPANY_EDIT_TABS,
            companyEditTab: COMPANY_EDIT_TABS.COMPANY,
            serverURL,
            activeTab: "personal",
            processing: false,
            loading: true,

            // master ids
            name: "",
            address: "",
            addressPincode: "",
            city: "",
            state: "",
            country: "",
            contactNumber: "",
            contactEmail: "",
            contactWebsite: "",
            workLocationIds: [],
            tanNo: "",
            registrationNo: "",
            pfApplicable: false,
            // documents
            // edit related fields
            editMode: true,
            editData: null,
            workLocationTypes: [],
            workLocations: [],

            // bank details
            bankMasterId: "",
            bankAccountNumber: "",
            bankAccountBankName: "",
            bankAccountBranch: "",
            bankAccountIfsc: "",
            bankAccountSwiftId: "",
        }
    },
    props: { videoId: { type: String, default: "" } },
    components: {
        CountryStateCitySelector,
        CustomInput,
        SelectDropdown,
        PayheadValuesEditForm,
    },
    async mounted() {
        const tab = this.$route.query.active_tab
        if (tab) {
            const found = Object.values(COMPANY_EDIT_TABS).find((item) => item.name === tab)
            if (found) this.companyEditTab = found
        }
        await this.initIfEditMode()
        await this.fetchWorkLocations()
    },
    computed: {
        ...mapState("misc", ["countries", "bankMasters"]),
    },
    methods: {
        handleTabChange(tab) {
            this.companyEditTab = tab
            const url = this.$router.resolve({
                ...this.$route,
                query: { ...this.$route.query, active_tab: tab.name },
            }).href
            this.$router.push(url)
        },
        handleCountryUpdate(update) {
            if (update) {
                this.country = update
            }
        },
        cancelEdit() {
            this.$router.push("/companies")
        },
        validateTAN(tan) {
            // Define a regular expression pattern for TAN validation
            const tanPattern = /^[A-Z]{4}\d{5}[A-Z]$/

            // Use the test method to check if the TAN matches the pattern
            return tanPattern.test(tan)
        },
        async fetchWorkLocations() {
            try {
                const res = await http.get("/work-locations")
                this.workLocations = res.data.result
            } catch (err) {
                console.log("error in fetching work locations", err)
            }
        },
        async initIfEditMode() {
            const id = this.$route.params.id
            if (id) {
                this.editMode = true
                const res = await http.get("/companies/" + id)
                this.editData = res.data.result

                // set the defaults to each field.
                if (this.editData) {
                    this.name = this.editData.name || ""
                    this.address = this.editData.address || ""
                    this.addressPincode = this.editData.addressPincode || ""
                    this.city = this.editData.city || ""
                    this.state = this.editData.state || ""
                    this.country = this.editData.country || ""

                    this.contactNumber = this.editData.contactNumber || ""
                    this.contactEmail = this.editData.contactEmail || ""
                    this.contactWebsite = this.editData.contactWebsite || ""
                    this.tanNo = this.editData.tanNo || ""
                    this.registrationNo = this.editData.registrationNo || ""
                    this.pfApplicable = this.editData.pfApplicable || false
                    this.workLocationIds = this.editData.workLocationIds || []

                    // bank related data
                    this.bankMasterId = this.editData.bankMasterId || ""
                    this.bankAccountNumber = this.editData.bankAccountNumber || ""
                    this.bankAccountBankName = this.editData.bankAccountBankName || ""
                    this.bankAccountBranch = this.editData.bankAccountBranch || ""
                    this.bankAccountIfsc = this.editData.bankAccountIfsc || ""
                    this.bankAccountSwiftId = this.editData.bankAccountSwiftId || ""
                }
            } else {
                this.editMode = false
            }
        },
        async createOrUpdate() {
            if (this.processing) return

            if (!this.name) {
                return this.$toast.error("Please enter Company name")
            }
            if (!this.country || !this.city || !this.state) {
                return this.$toast.error("Please enter country, state and city!")
            }
            if (!this.contactEmail || !this.contactNumber) {
                return this.$toast.error("Please enter contact number and contact email!")
            }

            if (!validateEmail(this.contactEmail)) {
                return this.$toast.error("Contact email address is not valid!")
            }
            if (!validatePhone(this.contactNumber)) {
                return this.$toast.error("Contact number must be 10 digits!")
            }

            if (!this.address || !this.addressPincode) {
                return this.$toast.error("Please enter company address and pincode!")
            }
            if (this.addressPincode.length != 6) {
                return this.$toast.error("Pincode must be 6 digits only!")
            }
            if (this.tanNo && !this.validateTAN(this.tanNo)) {
                return this.$toast.error("Tan No. is not valid!")
            }
            if (this.registrationNo && this.registrationNo.length < 5) {
                return this.$toast.error("Registration No. is not valid!")
            }

            this.processing = true
            const formDataObj = {
                name: this.name,
                address: this.address,
                addressPincode: this.addressPincode,
                city: this.city,
                state: this.state,
                country: this.country,

                contactNumber: this.contactNumber,
                contactEmail: this.contactEmail,
                contactWebsite: this.contactWebsite,
                tanNo: this.tanNo,
                registrationNo: this.registrationNo,
                pfApplicable: this.pfApplicable,
                workLocationIds: this.workLocationIds,

                // bank details
                bankMasterId: this.bankMasterId,
                bankAccountNumber: this.bankAccountNumber,
                bankAccountBankName: this.bankAccountBankName,
                bankAccountBranch: this.bankAccountBranch,
                bankAccountIfsc: this.bankAccountIfsc,
                bankAccountSwiftId: this.bankAccountSwiftId,
            }
            try {
                if (this.editMode) {
                    await http.patch(`/companies/${this.editData.id}`, formDataObj)
                } else {
                    await http.post("/companies", formDataObj)
                }
                this.$toast.success(
                    this.editMode ? "Company Updated Successfully" : "Company Created Successfully"
                )
                window.location.href = "/companies"
            } catch (err) {
                console.log("error in creating Company", err)
                this.$toast.error(
                    (err.response && err.response.data && err.response.data.message) ||
                        "Some error occured"
                )
            }
            this.processing = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.ce-page { padding:24px; max-width:1100px; margin:0 auto; }

/* Header */
.ce-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
.ce-header-left { display:flex; align-items:center; gap:12px; }
.ce-back-btn { width:36px; height:36px; border-radius:$radiusFull; border:1px solid $gray200; background:white; color:$gray500; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:13px; transition:all $transitionFast; &:hover { background:$gray50; color:$gray700; } }
.ce-title { font-size:20px; font-weight:700; color:$gray800; margin:0; }
.ce-subtitle { font-size:12.5px; color:$gray400; margin:2px 0 0; }

/* Tabs */
.ce-tabs { display:flex; gap:6px; margin-bottom:20px; border-bottom:2px solid $gray100; }
.ce-tab { display:inline-flex; align-items:center; gap:6px; padding:10px 18px; border:none; background:none; font-size:13px; font-weight:500; color:$gray500; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; font-family:inherit; transition:all $transitionFast; border-radius:$radiusSm $radiusSm 0 0; &:hover:not(.ce-tab-active) { color:$gray700; background:$gray50; } }
.ce-tab-active { color:$primaryColor; border-bottom-color:$primaryColor; font-weight:600; background:$primaryLight; }
.ce-tab-icon { font-size:12px; }

/* Sections */
.ce-section { background:white; border:1px solid $gray100; border-radius:10px; padding:20px; margin-bottom:16px; box-shadow:$shadowSm; }
.ce-section-head { font-size:14px; font-weight:700; color:$gray800; margin-bottom:16px; display:flex; align-items:center; gap:8px; padding-bottom:10px; border-bottom:1px solid $gray100; }
.ce-section-icon { color:$primaryColor; font-size:13px; }
.ce-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:6px 20px; }
.ce-field { }
.ce-field-full { grid-column:1 / -1; }
.ce-label { font-size:13px; font-weight:500; color:$gray600; margin-bottom:6px; display:block; }

/* Modern form inputs inside sections */
.ce-section ::v-deep .form-group { margin-bottom:4px; }
.ce-section ::v-deep .form-group label { font-size:12.5px; font-weight:600; color:$gray600; letter-spacing:.2px; }
.ce-section ::v-deep .form-control { border:1px solid $gray200; border-radius:8px; padding:9px 12px; font-size:13px; font-weight:500; color:$gray800; background:white; transition:all $transitionFast; box-shadow:none; height:auto; &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,.08); outline:none; } &:disabled { background:$gray50; color:$gray400; cursor:not-allowed; } }
.ce-section ::v-deep .form-control-sm { font-size:13px; padding:9px 12px; }

/* Actions */
.ce-actions { display:flex; justify-content:flex-end; gap:10px; padding:16px 0; }
.ce-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 20px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; border:none; font-family:inherit; transition:all $transitionFast; }
.ce-btn-primary { background:$primaryColor; color:white; &:hover { background:$primaryDark; } &:disabled { opacity:.6; cursor:not-allowed; } }
.ce-btn-secondary { background:white; color:$gray600; border:1px solid $gray200; &:hover { background:$gray50; } }

/* Loading */
.ce-loading { display:flex; align-items:center; justify-content:center; gap:8px; padding:60px 20px; font-size:14px; color:$gray400; i { font-size:18px; color:$primaryColor; } }

@media(max-width:768px) {
    .ce-page { padding:16px; }
    .ce-grid { grid-template-columns:1fr; }
    .ce-actions { flex-direction:column; }
    .ce-btn { justify-content:center; }
}
</style>
